import RangeSlider from 'components/Basic/RangeSlider/RangeSlider';
import { FilterCheckbox } from 'components/Blocks/Product/Filter/FilterCheckbox/FilterCheckbox';
import {
    filterGroupShowAllButtonTwClass,
    filterGroupTitleTwClass,
    filterGroupTwClass,
    getFilterGroupArrowTwClass,
    getFilterGroupContentItemTwClass,
    getFilterGroupContentTwClass,
} from 'components/Blocks/Product/Filter/FilterGroup/FilterGroup';
import CheckboxColor from 'components/Forms/CheckboxColor/CheckboxColor';
import { DefaultProductFiltersMapType } from 'config/constants';
import { formatParameterValue } from 'helpers/formaters/formatFilter';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import useTranslation from 'next-translate/useTranslation';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { useState } from 'react';
import { useSessionStore } from 'store/useSessionStore';
import { ParametersType } from 'types/productFilter';

type FilterGroupParametersProps = {
    title: string;
    parameterIndex: number;
    parameter: ParametersType;
    defaultNumberOfShownParameters: number;
};

export const FilterGroupParameters: FC<FilterGroupParametersProps> = ({
    title,
    defaultNumberOfShownParameters,
    parameter,
    parameterIndex,
}) => {
    const { t } = useTranslation();
    const [isGroupCollapsed, setIsGroupCollapsed] = useState(parameter.isCollapsed);
    const { updateFilterParametersQuery } = useUpdateFilterQuery();
    const filterQuery = useCurrentFilterQuery();
    const defaultSelectedParameters = useSessionStore((s) => s.defaultProductFiltersMap.parameters);

    const selectedParameter = filterQuery?.parameters?.find((p) => p.parameter === parameter.uuid);

    const isCheckboxType = parameter.__typename === 'ParameterCheckboxFilterOption';

    // we need to check everywhere for isCheckboxType, otherwise Typescript doesn't know if .values exists
    // that's because it is sure only about overlaps within types of parameter
    const hiddenOptions = isCheckboxType
        ? parameter.values.slice(defaultNumberOfShownParameters, parameter.values.length)
        : [];

    const isWithHiddenCheckedItem = isCheckboxType
        ? hiddenOptions.some((o) => !!selectedParameter?.values?.includes(o.uuid))
        : false;

    const [isWithAllItemsShown, setIsWithAllItemsShown] = useState(isWithHiddenCheckedItem);

    const shownOptions = isCheckboxType ? parameter.values.slice(0, defaultNumberOfShownParameters) : [];
    const defaultOptions = isCheckboxType ? (isWithAllItemsShown ? parameter.values : shownOptions) : [];

    return (
        <div className={filterGroupTwClass}>
            <div className={filterGroupTitleTwClass} onClick={() => setIsGroupCollapsed((prev) => !prev)}>
                {title}
                <ArrowIcon className={getFilterGroupArrowTwClass(!isGroupCollapsed)} />
            </div>
            {!isGroupCollapsed && (
                <div className={getFilterGroupContentTwClass(!isGroupCollapsed)}>
                    {isCheckboxType && (
                        <>
                            {defaultOptions.map((parameterValueOption, index) => {
                                if (!parameterValueOption.text) {
                                    return null;
                                }

                                const isChecked = getIsSelectedParameterValue(
                                    defaultSelectedParameters,
                                    selectedParameter?.values,
                                    parameter.uuid,
                                    parameterValueOption.uuid,
                                );
                                const id = `parameters.${parameterIndex}.values.${index}.checked`;
                                const isDisabled = parameterValueOption.count === 0 && !isChecked;

                                return (
                                    <div
                                        key={parameterValueOption.uuid}
                                        className={getFilterGroupContentItemTwClass(isDisabled, isChecked)}
                                    >
                                        <FilterCheckbox
                                            count={parameterValueOption.count}
                                            disabled={isDisabled}
                                            id={id}
                                            name={id}
                                            value={isChecked}
                                            label={formatParameterValue(
                                                parameterValueOption.text,
                                                parameter.isLogicalType,
                                                t,
                                            )}
                                            onChange={() =>
                                                updateFilterParametersQuery(parameter.uuid, parameterValueOption.uuid)
                                            }
                                        />
                                    </div>
                                );
                            })}
                            {!!hiddenOptions.length && (
                                <button
                                    className={filterGroupShowAllButtonTwClass}
                                    tabIndex={0}
                                    onClick={() => setIsWithAllItemsShown((prev) => !prev)}
                                >
                                    {isWithAllItemsShown ? t('show less') : t('show more')}
                                </button>
                            )}
                        </>
                    )}
                    {parameter.__typename === 'ParameterColorFilterOption' && (
                        <div className="flex flex-row flex-wrap gap-2">
                            {parameter.values.map((parameterValue, index) => {
                                if (!parameterValue.text) {
                                    return null;
                                }

                                const isChecked = getIsSelectedParameterValue(
                                    defaultSelectedParameters,
                                    selectedParameter?.values,
                                    parameter.uuid,
                                    parameterValue.uuid,
                                );
                                const id = `parameters.${parameterIndex}.values.${index}.checked`;

                                return (
                                    <CheckboxColor
                                        key={parameterValue.uuid}
                                        colorImage={parameterValue.mainImage?.url ?? null}
                                        colorName={parameterValue.text}
                                        colorValue={parameterValue.rgbHex ?? undefined}
                                        id={id}
                                        name={id}
                                        value={isChecked}
                                        onChange={() =>
                                            updateFilterParametersQuery(parameter.uuid, parameterValue.uuid)
                                        }
                                    />
                                );
                            })}
                        </div>
                    )}
                    {parameter.__typename === 'ParameterSliderFilterOption' && (
                        <RangeSlider
                            isDisabled={!parameter.isSelectable}
                            max={parameter.maximalValue}
                            maxValue={selectedParameter?.maximalValue ?? parameter.maximalValue}
                            min={parameter.minimalValue}
                            minValue={selectedParameter?.minimalValue ?? parameter.minimalValue}
                            setMaxValueCallback={(value) =>
                                updateFilterParametersQuery(
                                    parameter.uuid,
                                    undefined,
                                    selectedParameter?.minimalValue ?? undefined,
                                    parameter.maximalValue === value ? undefined : value,
                                )
                            }
                            setMinValueCallback={(value) =>
                                updateFilterParametersQuery(
                                    parameter.uuid,
                                    undefined,
                                    parameter.minimalValue === value ? undefined : value,
                                    selectedParameter?.maximalValue ?? undefined,
                                )
                            }
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const getIsSelectedParameterValue = (
    defaultSelectedParameters: DefaultProductFiltersMapType['parameters'],
    parameterValues: string[] | undefined,
    parameterUuid: string,
    parameterValueUuid: string,
) => {
    const isSelectedByDefault = !!defaultSelectedParameters.get(parameterUuid)?.has(parameterValueUuid);
    const isSelectedByUser = !!parameterValues?.includes(parameterValueUuid) || isSelectedByDefault;

    return isSelectedByDefault || isSelectedByUser;
};
